import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/store/hooks';
import { logout, initializeAuth, switchBack } from '../redux/features/authSlice';
import { Sidebar } from '../components/layout/sidebar';
import { Header } from '../components/layout/header';
import { Activity, Loader2, Undo2, UserCog } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { publishLocalPrinters } from '../utils/devicePublish';
import { startRemotePrinterSync, stopRemotePrinterSync } from '../utils/remotePrinters';
import { startPrintSocket, stopPrintSocket } from '../utils/printSocket';
import { RemotePrintOfflineModal } from '../components/print/RemotePrintOfflineModal';
import { queryClient } from '../lib/queryClient';

export function MainLayout() {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated, isLoading } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [switchingBack, setSwitchingBack] = useState(false);

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Publish this PC's shared printers to the global registry on login so other
  // workstations can discover them with the host auto-filled — no manual setup.
  // Also keep the other-PC printer list the local agent's UI shows in sync, so
  // the agent is current as soon as the dashboard opens (not only on the Devices
  // page) and picks up printers registered later without a reload.
  useEffect(() => {
    if (!isAuthenticated) return;
    void publishLocalPrinters();
    startRemotePrinterSync();
    return () => stopRemotePrinterSync();
  }, [isAuthenticated]);

  // Listen for remote print jobs pushed by the backend and print them through
  // this PC's local agent. Only relevant where an agent runs; elsewhere the
  // registration is a no-op.
  useEffect(() => {
    if (!isAuthenticated) return;
    startPrintSocket();
    return () => stopPrintSocket();
  }, [isAuthenticated]);

  const handleLogout = async () => {
    await dispatch(logout()).unwrap();
    // Query keys are not user-scoped, and the non-auth Redux slices keep their
    // lists. Drop the client cache and reload before the next sign-in so a
    // shared workstation can never render the previous user's cached data.
    // (Impersonation already does a full reload for the same reason.)
    queryClient.clear();
    window.location.assign('/login');
  };

  const handleSwitchBack = async () => {
    try {
      setSwitchingBack(true);
      await dispatch(switchBack()).unwrap();
      toast.success('Returned to your account');
      // Full reload so every cached query/state is rebuilt for the restored
      // super admin identity.
      window.location.assign('/');
    } catch (error: any) {
      toast.error(typeof error === 'string' ? error : 'Failed to switch back');
      setSwitchingBack(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex items-center gap-3 text-text-muted">
          <Activity className="w-5 h-5 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="flex h-full bg-background">
      <Sidebar
        user={user}
        onLogout={handleLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        {user?.impersonating && (
          <div className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-500/15 border-b border-amber-500/40 text-amber-900 dark:text-amber-200 text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <UserCog size={16} className="shrink-0" />
              <span className="truncate">
                Viewing as <strong>{user.name}</strong>
                {user.impersonatorName ? (
                  <>
                    {' '}
                    &mdash; signed in as <strong>{user.impersonatorName}</strong>
                  </>
                ) : null}
              </span>
            </div>
            <button
              type="button"
              onClick={handleSwitchBack}
              disabled={switchingBack}
              className="shrink-0 px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
            >
              {switchingBack ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Undo2 size={14} />
              )}
              Switch Back
            </button>
          </div>
        )}
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className="flex-1 overflow-auto p-4 lg:p-6 min-h-0">
          <Outlet />
        </main>
      </div>
      <Toaster position="bottom-right" richColors closeButton />
      <RemotePrintOfflineModal />
    </div>
  );
}
